import api from '@/services/Api';
import Briefart from '@/types/Briefart';
import { successSwal, warningSwal } from '@/helper/swals';
import { ActionContext, Module } from 'vuex';
import { AxiosResponse } from 'axios';
import { BriefartenState, RootState } from '@/store/types';
import { DataResponse } from '@/types';

export type BriefartenFilter = {
    gueltig: string;
};

export type LoadBriefartenPayload = {
    filter: BriefartenFilter;
};

export type UpdateBriefartenPayload = {
    briefart: Briefart;
    filter: BriefartenFilter;
};

export type DeleteBriefartenPayload = {
    briefart: Partial<Briefart>;
    filter: BriefartenFilter;
};

export type BriefartenLoadData = DataResponse<Partial<Briefart>[], { platzhalter: string[] }>;

export type SetBriefartenDialogs = {
    edit?: boolean;
};

export type SetBriefartenLoaders = {
    save?: boolean;
    delete?: boolean;
    briefarten?: boolean;
};

type Context = ActionContext<BriefartenState, RootState>;

export const briefarten: Module<BriefartenState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            edit: false,
        },
        loaders: {
            save: false,
            delete: false,
            briefarten: false,
        },
        briefarten: [],
        platzhalter: [],
    },
    actions: {
        loadBriefarten({ commit }: Context, payload?: LoadBriefartenPayload): void {
            commit('setLoaders', { briefarten: true });

            const data = {
                ...payload,
            };

            api.post<BriefartenLoadData>('verwaltung/briefarten/load', data)
                .then((response) => commit('setBriefarten', response.data))
                .catch(console.error)
                .finally(() => commit('setLoaders', { briefarten: false }));
        },
        updateBriefart({ commit, dispatch, rootState }: Context, { briefart, filter }: UpdateBriefartenPayload): void {
            commit('setLoaders', { save: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...briefart,
            };

            api.post('verwaltung/briefarten/speichern', data)
                .then(() => {
                    successSwal('Briefart', 'gespeichert');
                    dispatch('loadBriefarten', { filter });
                    commit('setDialogs', { edit: false });
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { save: false }));
        },
        deleteBriefart({ commit, dispatch, rootState }: Context,
                       { briefart: { briefart_id }, filter }: DeleteBriefartenPayload): void {
            commit('setLoaders', { delete: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                briefart_id,
            };

            api.post('verwaltung/briefarten/loeschen', data)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body === 'string') {
                        warningSwal('Löschung', response.data.body ?? 'Briefart ist nicht löschbar.');
                    } else {
                        successSwal('Briefart', 'gelöscht');
                        dispatch('loadBriefarten', { filter });
                        commit('setDialogs', { edit: false });
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { delete: false }));
        },
    },
    mutations: {
        setBriefarten(state: BriefartenState, { body, selections }: BriefartenLoadData): void {
            state.briefarten = body.map((data) => new Briefart(data));
            state.platzhalter = selections.platzhalter;
        },
        setDialogs(state: BriefartenState, dialogs: SetBriefartenDialogs): void {
            state.dialogs = {
                ...state.dialogs,
                ...dialogs,
            };
        },
        setLoaders(state: BriefartenState, loaders: SetBriefartenLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...loaders,
            };
        },
    },
};
